interface SkeletonProps {
  className?: string
}

export function Skeleton({ className = '' }: SkeletonProps) {
  return <span className={`cf-skeleton ${className}`.trim()} aria-hidden />
}

interface CardListSkeletonProps {
  count?: number
  label?: string
}

/** Skeleton de cards empilhados (alertas, pendências). */
export function CardListSkeleton({ count = 3, label = 'Carregando…' }: CardListSkeletonProps) {
  return (
    <div className='cf3-skeleton-list' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className='cf3-skeleton-card'>
          <div className='cf3-skeleton-card__header'>
            <Skeleton className='cf-skeleton--circle' />
            <div className='cf3-skeleton-card__lines'>
              <Skeleton className='cf-skeleton--title' />
              <Skeleton className='cf-skeleton--badge' />
            </div>
          </div>
          <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
          <Skeleton className='cf-skeleton--line' />
          <div className='cf3-skeleton-card__footer'>
            <Skeleton className='cf-skeleton--line cf-skeleton--short' />
            <Skeleton className='cf-skeleton--btn' />
          </div>
        </div>
      ))}
    </div>
  )
}

interface FilterRowSkeletonProps {
  columns?: number
}

export function FilterRowSkeleton({ columns = 3 }: FilterRowSkeletonProps) {
  return (
    <div className='cf3-skeleton-filters' aria-hidden>
      <Skeleton className='cf-skeleton--input' />
      {Array.from({ length: columns - 1 }, (_, i) => (
        <Skeleton key={i} className='cf-skeleton--input cf-skeleton--input-sm' />
      ))}
    </div>
  )
}

interface DocumentosPageSkeletonProps {
  label?: string
}

/** Skeleton da galeria de documentos (stats + toolbar + tiles). */
export function DocumentosPageSkeleton({
  label = 'Carregando arquivos…',
}: DocumentosPageSkeletonProps) {
  return (
    <div className='cf3-skeleton-page' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <div className='cf3-skeleton-stats' aria-hidden>
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className='cf-skeleton--stat' />
        ))}
      </div>
      <Skeleton className='cf-skeleton--toolbar' />
      <div className='cf3-skeleton-gallery' aria-hidden>
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className='cf-skeleton--doc-tile' />
        ))}
      </div>
    </div>
  )
}

interface PageHeaderSkeletonProps {
  label?: string
}

export function PageHeaderSkeleton({ label = 'Carregando…' }: PageHeaderSkeletonProps) {
  return (
    <div className='cf3-skeleton-header' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <Skeleton className='cf-skeleton--title cf-skeleton--hero' />
      <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
    </div>
  )
}

interface PassoAtualSkeletonProps {
  label?: string
}

/** Skeleton do hero do passo atual (Guia). */
export function PassoAtualSkeleton({ label = 'Carregando etapa…' }: PassoAtualSkeletonProps) {
  return (
    <div className='cf3-skeleton-passo' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <Skeleton className='cf-skeleton--line cf-skeleton--short' />
      <Skeleton className='cf-skeleton--title cf-skeleton--passo' />
      <div className='cf3-skeleton-passo__stats' aria-hidden>
        <Skeleton className='cf-skeleton--stat cf-skeleton--stat-sm' />
        <Skeleton className='cf-skeleton--stat cf-skeleton--stat-sm' />
      </div>
      <div className='cf3-skeleton-passo__actions' aria-hidden>
        <Skeleton className='cf-skeleton--btn cf-skeleton--btn-wide' />
        <Skeleton className='cf-skeleton--btn' />
      </div>
    </div>
  )
}

interface FinanceiroPageSkeletonProps {
  label?: string
}

/** Skeleton do financeiro (header + tabs + card saldo). */
export function FinanceiroPageSkeleton({
  label = 'Carregando finanças…',
}: FinanceiroPageSkeletonProps) {
  return (
    <div className='cf3-skeleton-fin' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <PageHeaderSkeleton label={label} />
      <div className='cf3-skeleton-fin__tabs' aria-hidden>
        <Skeleton className='cf-skeleton--chip' />
        <Skeleton className='cf-skeleton--chip' />
        <Skeleton className='cf-skeleton--chip' />
      </div>
      <Skeleton className='cf-skeleton--fin-card' />
      <CardListSkeleton count={2} label={label} />
    </div>
  )
}

interface MateriaisPageSkeletonProps {
  label?: string
}

/** Skeleton de cotações (header + toolbar + grupos). */
export function MateriaisPageSkeleton({
  label = 'Carregando orçamentos…',
}: MateriaisPageSkeletonProps) {
  return (
    <div className='cf3-skeleton-page' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <PageHeaderSkeleton label={label} />
      <div className='cf3-skeleton-mat__toolbar' aria-hidden>
        <Skeleton className='cf-skeleton--btn cf-skeleton--btn-wide' />
        <Skeleton className='cf-skeleton--btn' />
      </div>
      {Array.from({ length: 2 }, (_, i) => (
        <div key={i} className='cf3-skeleton-mat__grupo' aria-hidden>
          <div className='cf3-skeleton-card__header'>
            <div className='cf3-skeleton-card__lines'>
              <Skeleton className='cf-skeleton--title' />
              <Skeleton className='cf-skeleton--line cf-skeleton--short' />
            </div>
            <Skeleton className='cf-skeleton--btn' />
          </div>
          <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
          <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
        </div>
      ))}
    </div>
  )
}

interface JornadaPageSkeletonProps {
  label?: string
}

/** Skeleton da jornada (hero + busca + abas + módulos). */
export function JornadaPageSkeleton({ label = 'Carregando jornada…' }: JornadaPageSkeletonProps) {
  return (
    <div className='cf3-skeleton-jornada' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <div className='cf3-skeleton-jornada__hero' aria-hidden>
        <div className='cf3-skeleton-jornada__hero-head'>
          <Skeleton className='cf-skeleton--title cf-skeleton--jornada-title' />
          <Skeleton className='cf-skeleton--circle cf-skeleton--ring' />
        </div>
        <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
        <Skeleton className='cf-skeleton--line cf-skeleton--short' />
        <Skeleton className='cf-skeleton--btn cf-skeleton--btn-wide' />
      </div>
      <Skeleton className='cf-skeleton--input' />
      <div className='cf3-skeleton-fin__tabs' aria-hidden>
        <Skeleton className='cf-skeleton--chip cf-skeleton--chip-wide' />
        <Skeleton className='cf-skeleton--chip' />
        <Skeleton className='cf-skeleton--chip' />
      </div>
      <div className='cf3-skeleton-jornada__modulos' aria-hidden>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className='cf3-skeleton-jornada__modulo'>
            <Skeleton className='cf-skeleton--title' />
            <Skeleton className='cf-skeleton--line cf-skeleton--wide cf-skeleton--progress' />
          </div>
        ))}
      </div>
    </div>
  )
}

interface PassoTopSkeletonProps {
  label?: string
}

/** Skeleton da página de etapa (trail + título + abas + conteúdo). */
export function PassoTopSkeleton({ label = 'Carregando etapa…' }: PassoTopSkeletonProps) {
  return (
    <div className='cf3-skeleton-passo-top' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <Skeleton className='cf-skeleton--line cf-skeleton--short' />
      <Skeleton className='cf-skeleton--passo cf-skeleton--passo-title' />
      <div className='cf3-skeleton-fin__tabs' aria-hidden>
        <Skeleton className='cf-skeleton--chip cf-skeleton--chip-wide' />
        <Skeleton className='cf-skeleton--chip cf-skeleton--chip-wide' />
      </div>
      <div className='cf3-skeleton-passo-top__content' aria-hidden>
        <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
        <Skeleton className='cf-skeleton--line' />
        <Skeleton className='cf-skeleton--stat cf-skeleton--stat-sm' />
        <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
        <Skeleton className='cf-skeleton--line' />
      </div>
    </div>
  )
}

interface LeituraSkeletonProps {
  label?: string
}

/** Skeleton da leitura complementar do guia. */
export function LeituraSkeleton({ label = 'Carregando orientação…' }: LeituraSkeletonProps) {
  return (
    <div className='cf3-leitura cf3-leitura--skeleton' role='status' aria-busy='true'>
      <span className='cf-sr-only'>{label}</span>
      <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
      {Array.from({ length: 2 }, (_, i) => (
        <div key={i} className='cf3-skeleton-leitura__block' aria-hidden>
          <Skeleton className='cf-skeleton--title cf-skeleton--block-title' />
          <Skeleton className='cf-skeleton--line cf-skeleton--wide' />
          <Skeleton className='cf-skeleton--line' />
          <Skeleton className='cf-skeleton--line cf-skeleton--short' />
        </div>
      ))}
    </div>
  )
}
